import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Dialog, Textarea, useToast } from '@artosera/ui';
import { formaterDate } from '../../../../lib/format.js';
import {
  ecarterLignes,
  MOTIF_ECARTEMENT_MAX,
  MOTIF_ECARTEMENT_MIN,
  telechargerRejets,
  type CibleRejets,
} from '../../../../lib/plan.js';
import type { LotReprise, NatureLot } from '../../../../lib/reprise.js';
import { messageReprise } from '../messages.js';
import reprise from '../Reprise.module.css';
import styles from './Plan.module.css';

/** Ce que l'écran sait des lignes refusées d'un lot, ou des morceaux d'un lot qui se découpe. */
function etatDesRefus(lot: LotReprise): {
  refuses: number;
  ecartes: number;
  /** Un lot ou des morceaux attendent encore l'écartement. */
  aEcarter: boolean;
  cibles: Array<CibleRejets & { lignes: number }>;
} {
  const morceaux = lot.morceaux?.liste ?? [];
  if (morceaux.length > 0) {
    const essayes = morceaux.filter((morceau) => morceau.etat === 'essaye');
    const parNature = new Map<NatureLot, number>();
    for (const morceau of morceaux) {
      const lignes = morceau.compteurs?.refuses ?? 0;
      if (lignes > 0) parNature.set(morceau.nature, (parNature.get(morceau.nature) ?? 0) + lignes);
    }
    return {
      refuses: essayes.reduce((total, morceau) => total + (morceau.compteurs?.refuses ?? 0), 0),
      ecartes: lot.morceaux?.compteurs.ecartes ?? 0,
      aEcarter: essayes.length > 0,
      cibles: [...parNature.entries()].map(([nature, lignes]) => ({
        feuille: 'principal',
        nature,
        lignes,
      })),
    };
  }
  return {
    refuses: lot.compteurs?.refuses ?? 0,
    ecartes: lot.compteurs?.ecartes ?? lot.ecartes?.nombre ?? 0,
    aEcarter: lot.etat === 'essaye' && (lot.compteurs?.refuses ?? 0) > 0,
    cibles: (lot.rejets ?? []).map(({ feuille, lignes }) => ({ feuille, lignes })),
  };
}

/**
 * L'écartement des lignes refusées à l'essai (arbitrage A4). Un lot dont
 * l'essai comporte des refus peut en écarter les lignes, avec un motif
 * obligatoire : elles ne seront pas reprises, le fichier des rejets les garde,
 * et le lot devient prêt. Le bloc montre aussi ce qui a été écarté, par qui et
 * pourquoi, et rend le fichier des rejets (CSV). Les nombres sont ceux de
 * l'API (`compteurs.refuses`, `compteurs.ecartes`, `ecartes`).
 */
export function EcartementBloc({
  idGalerie,
  lot,
  peutAgir,
  onLot,
}: {
  idGalerie: string;
  lot: LotReprise;
  peutAgir: boolean;
  onLot: (lot: LotReprise) => void;
}): JSX.Element | null {
  const { t } = useTranslation('console');
  const client = useQueryClient();
  const { toast } = useToast();
  const [ouvert, setOuvert] = useState(false);
  const [motif, setMotif] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [erreurTelechargement, setErreurTelechargement] = useState<string | null>(null);
  const etat = etatDesRefus(lot);
  const parMorceaux = (lot.morceaux?.liste.length ?? 0) > 0;

  const net = motif.trim();
  const motifValide = net.length >= MOTIF_ECARTEMENT_MIN && net.length <= MOTIF_ECARTEMENT_MAX;

  const ecarter = useMutation({
    mutationFn: () => ecarterLignes(idGalerie, lot.id, net),
    onSuccess: (suite) => {
      setOuvert(false);
      setMotif('');
      setErreur(null);
      toast({
        title: t('plan.ecartement.ok', { count: etat.refuses }),
        closeLabel: t('fiche.domaines.fermer'),
      });
      onLot(suite);
      void client.invalidateQueries({
        queryKey: ['console', 'galerie', idGalerie, 'reprise', 'lot', lot.id],
      });
    },
    onError: (cause) => {
      setErreur(messageReprise(t, cause));
      // Le lot a pu changer sous nos yeux (`lot_modifie`, `essai_a_refaire`).
      void client.invalidateQueries({
        queryKey: ['console', 'galerie', idGalerie, 'reprise', 'lot', lot.id],
      });
    },
  });

  const telecharger = async (cible: CibleRejets): Promise<void> => {
    setErreurTelechargement(null);
    try {
      await telechargerRejets(idGalerie, lot.id, cible);
    } catch (cause) {
      setErreurTelechargement(messageReprise(t, cause));
    }
  };

  if (!etat.aEcarter && etat.ecartes === 0) return null;

  const boutonsRejets = etat.cibles.map((cible) => (
    <Button
      key={`${cible.nature ?? ''}/${cible.feuille}`}
      variant="secondary"
      onClick={() => void telecharger(cible)}
    >
      {cible.nature
        ? t('plan.ecartement.telechargerNature', {
            nature: t(`reprise.natures.${cible.nature}`),
          })
        : cible.feuille === 'principal'
          ? t('plan.ecartement.telecharger')
          : t('plan.ecartement.telechargerFeuille', { feuille: cible.feuille })}
      {' · '}
      {t('plan.ecartement.lignes', { count: cible.lignes })}
    </Button>
  ));

  return (
    <div className={styles.ecartement}>
      <h3>{etat.aEcarter ? t('plan.ecartement.titre') : t('plan.ecartement.fait.titre')}</h3>

      {etat.aEcarter ? (
        <>
          <p>
            {t(parMorceaux ? 'plan.ecartement.explicationMorceaux' : 'plan.ecartement.explication')}
          </p>
          {peutAgir ? (
            <div className={reprise.actions}>
              <Button onClick={() => setOuvert(true)}>
                {t('plan.ecartement.bouton', { count: etat.refuses })}
              </Button>
            </div>
          ) : null}
        </>
      ) : (
        <>
          {lot.ecartes ? (
            <>
              <p>
                {t('plan.ecartement.fait.resume', {
                  count: etat.ecartes,
                  par: lot.ecartes.par,
                  date: formaterDate(lot.ecartes.le, '—'),
                })}
              </p>
              <p>{t('plan.ecartement.fait.motif', { motif: lot.ecartes.motif })}</p>
            </>
          ) : (
            <p>
              {t('plan.ecartement.fait.compteur')} : {etat.ecartes}
            </p>
          )}
          <p className={reprise.discret}>{t('plan.ecartement.fait.aide')}</p>
        </>
      )}

      {boutonsRejets.length > 0 ? <div className={reprise.actions}>{boutonsRejets}</div> : null}
      {erreurTelechargement ? (
        <p className={reprise.erreur} role="alert">
          {erreurTelechargement}
        </p>
      ) : null}

      <Dialog
        open={ouvert}
        onOpenChange={(estOuvert) => {
          setOuvert(estOuvert);
          if (!estOuvert) setErreur(null);
        }}
        title={t('plan.ecartement.dialogue.titre')}
        description={t('plan.ecartement.dialogue.texte', { count: etat.refuses })}
        closeLabel={t('plan.ecartement.dialogue.garder')}
        footer={
          <>
            <Button variant="ghost" onClick={() => setOuvert(false)}>
              {t('plan.ecartement.dialogue.garder')}
            </Button>
            <Button
              loading={ecarter.isPending}
              disabled={!motifValide}
              onClick={() => ecarter.mutate()}
            >
              {t('plan.ecartement.dialogue.confirmer')}
            </Button>
          </>
        }
      >
        <div className={styles.motif}>
          <label htmlFor="ecartement-motif">{t('plan.ecartement.dialogue.motif')}</label>
          <Textarea
            id="ecartement-motif"
            value={motif}
            maxLength={MOTIF_ECARTEMENT_MAX + 100}
            required
            aria-describedby="ecartement-motif-aide"
            aria-invalid={motif !== '' && !motifValide}
            onChange={(evenement) => setMotif(evenement.target.value)}
          />
          <div className={styles.motifPied} id="ecartement-motif-aide">
            <span>
              {net.length > MOTIF_ECARTEMENT_MAX
                ? t('plan.ecartement.dialogue.motifTropLong')
                : motifValide
                  ? t('plan.ecartement.dialogue.motifAide')
                  : t('plan.ecartement.dialogue.motifRequis')}
            </span>
            <span>
              {t('plan.ecartement.dialogue.compteur', {
                nombre: net.length,
                max: MOTIF_ECARTEMENT_MAX,
              })}
            </span>
          </div>
        </div>
        {erreur ? (
          <p className={reprise.erreur} role="alert">
            {erreur}
          </p>
        ) : null}
      </Dialog>
    </div>
  );
}
